import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getSettings, updateSettings } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { buildEmptySalesCatalog, normalizeSalesCatalog } from '../../../lib/salesCatalog';
import { POS_DEFAULT_TERMINAL_THEME } from '../../../lib/posCategoryAppearance';
import { buildDefaultPosHomeShortcuts, normalizePosHomeShortcuts } from '../../../lib/posHomeScreen';

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export function slugify(value) {
  return (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'webshop';
}

function buildTokenSignature(slug) {
  return slugify(slug)
    .split('-')
    .filter(Boolean)
    .join('');
}

function looksLikeMalformedSlug(rawSlug, defaultSlug) {
  const safeRaw = slugify(rawSlug);
  const safeDefault = slugify(defaultSlug);
  if (!safeRaw || !safeDefault || safeRaw === safeDefault) return false;

  const rawSignature = buildTokenSignature(safeRaw);
  const defaultSignature = buildTokenSignature(safeDefault);

  if (safeRaw.startsWith(`${safeDefault}-`) || safeRaw.endsWith(`-${safeDefault}`)) {
    return true;
  }

  if (rawSignature.length > defaultSignature.length && (rawSignature.includes(defaultSignature) || defaultSignature.includes(rawSignature))) {
    return Math.abs(rawSignature.length - defaultSignature.length) <= Math.max(4, Math.floor(defaultSignature.length * 0.4));
  }

  return false;
}

export function buildWebshopUrl(slug) {
  const safeSlug = slugify(slug);
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://digitalergastro-kassaapp.web.app';
  return `${origin}/webshop/${safeSlug}`;
}

function buildDefaultWebshopSlug(companyName, cityName) {
  const nameSlug = slugify(companyName);
  const citySlug = slugify(cityName);

  if (!citySlug) return nameSlug;
  if (nameSlug.includes(citySlug)) return nameSlug;
  if (citySlug.includes(nameSlug)) return citySlug;

  return slugify(`${companyName} ${cityName}`);
}

export function sanitizeWebshopSlug(rawSlug, companyName, cityName) {
  const defaultSlug = buildDefaultWebshopSlug(companyName, cityName);
  const safeRaw = slugify(rawSlug);

  if (!safeRaw) return defaultSlug;
  if (safeRaw === 'webshop' && defaultSlug !== 'webshop') return defaultSlug;
  if (looksLikeMalformedSlug(safeRaw, defaultSlug)) return defaultSlug;

  return safeRaw;
}

function isObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function mergeDeep(base, override) {
  if (Array.isArray(base)) {
    return Array.isArray(override) ? override : base;
  }
  if (!isObject(base)) {
    return override ?? base;
  }

  const next = { ...base };
  for (const key of Object.keys(override || {})) {
    const baseValue = base[key];
    const overrideValue = override[key];
    if (isObject(baseValue) && isObject(overrideValue)) {
      next[key] = mergeDeep(baseValue, overrideValue);
    } else {
      next[key] = overrideValue;
    }
  }
  return next;
}

function buildOpeningHours() {
  return {
    monday: { enabled: true, slots: [['11:00', '14:00'], ['17:00', '22:00']] },
    tuesday: { enabled: false, slots: [['11:00', '22:00']] },
    wednesday: { enabled: false, slots: [['11:00', '22:00']] },
    thursday: { enabled: false, slots: [['11:00', '22:00']] },
    friday: { enabled: true, slots: [['11:00', '14:00'], ['17:00', '22:00']] },
    saturday: { enabled: true, slots: [['11:00', '22:00']] },
    sunday: { enabled: true, slots: [['11:00', '22:00']] },
  };
}

function buildDefaultPortalConfig(settings, user) {
  const companyName = settings?.company_name || user?.name || 'FiscalPOS';
  const locationName = settings?.company_city || '';
  const publicSlug = sanitizeWebshopSlug('', companyName, locationName);
  const publicUrl = buildWebshopUrl(publicSlug);

  return {
    locationPresence: {
      locationName: companyName,
      about:
        'Traditionelle Gastfreundschaft, moderne Online-Bestellung und ein klares Markenerlebnis auf jedem Touchpoint.',
      address: settings?.company_address || '',
      email: '',
      phone: '',
      website: '',
      social: {
        instagram: '',
        facebook: '',
        tiktok: '',
      },
      legal: {
        imprint: '',
        privacy: '',
      },
      invoice: {
        registrationNumber: '',
        taxNumber: settings?.company_uid || '',
      },
    },
    appCustomers: {
      crmPlaybooks: [
        'Newsletter-Abonnenten beim Checkout aktivieren',
        'Loyalty Push-Kampagnen für Stammgäste starten',
        'Inaktive Kunden nach 30 Tagen automatisch ansprechen',
      ],
    },
    notifications: [],
    menus: {
      activeMenus: [
        { id: 'menu-standard', name: 'Standard Menu', type: 'all', visible: true, categoryIds: [] },
        { id: 'menu-delivery', name: 'Delivery Menu', type: 'delivery', visible: true, categoryIds: [] },
        { id: 'menu-pickup', name: 'Pickup Menu', type: 'pickup', visible: true, categoryIds: [] },
      ],
      featuredCategoryIds: [],
      featuredProductIds: [],
    },
    salesCatalog: buildEmptySalesCatalog(),
    dailyMenu: {
      headline: 'Mittagsmenü',
      subline: 'Frisch gekocht und täglich wechselnd.',
      days: WEEKDAYS.reduce((acc, day) => {
        acc[day] = { enabled: day !== 'sunday', title: '', productIds: [] };
        return acc;
      }, {}),
    },
    orderSettings: {
      openingHours: buildOpeningHours(),
      orderTimes: {
        pickupLeadMinutes: 20,
        deliveryLeadMinutes: 35,
        preorderDays: 7,
        slotIntervalMinutes: 15,
      },
      closures: [],
      orderTypes: {
        pickup: true,
        delivery: true,
        dineIn: false,
        scheduled: true,
      },
      deliveryFees: [
        { id: 'zone-1', zone: '0-3 km', fee: 2.5, minOrder: 15 },
        { id: 'zone-2', zone: '3-6 km', fee: 4.5, minOrder: 25 },
      ],
      thirdParty: {
        foodora: {
          enabled: true,
          status: 'Connected',
          storeId: 'foodora-piu-piano',
          webhookUrl: '',
          autoAccept: true,
        },
        lieferando: {
          enabled: true,
          status: 'Connected',
          storeId: 'lieferando-piu-piano',
          webhookUrl: '',
          autoAccept: false,
        },
        webshop: {
          enabled: true,
          status: 'Live',
          storeId: publicSlug,
          webhookUrl: '',
          autoAccept: true,
        },
      },
      articleMappings: [],
      cashRegisterConfig: {
        terminalId: settings?.terminal_id || 'POS_001',
        kassenId: settings?.kassen_id || 'KASSE001',
        autoPushToKitchen: true,
        autoSyncCompletedOrders: true,
      },
      payment: {
        cash: true,
        card: true,
        stripe: true,
        payOnPickup: true,
        invoice: false,
      },
      tablesQr: {
        enableTableSelection: false,
        defaultRoomId: '',
        qrPrefix: 'TABLE',
      },
      orderSources: [
        { id: 'website', label: 'Webshop', enabled: true },
        { id: 'foodora', label: 'Foodora', enabled: true },
        { id: 'lieferando', label: 'Lieferando', enabled: true },
        { id: 'wolt', label: 'Wolt', enabled: false },
      ],
      general: {
        notesPrompt: true,
        printProductionSlip: true,
        allowGuestCheckout: true,
        serviceFeePercent: 0,
      },
    },
    webshop: {
      currentUrl: publicUrl,
      urlSlug: publicSlug,
      enableTableSelection: false,
      showNewsletterOptIn: true,
      readOnlyMode: false,
      skipAppLandingPage: false,
      heroTitle: 'Order your way!',
      heroSubtitle: 'Delivery • Pick up',
      primaryColor: '#71c545',
      accentColor: '#082836',
      surfaceColor: '#e8f4eb',
    },
    pickupDisplay: {
      enabled: true,
      title: 'Pickup display',
      subtitle: 'Zeigt abholbereite Webshop-Bestellungen für Gäste und Frontoffice.',
      accentColor: '#71c545',
      autoClearSeconds: 45,
      showCustomerName: false,
    },
    posTerminal: {
      designPreset: 'gastronovi_classic',
      orderPanelWidth: 'normal', // normal | wide | xwide — width of the order slip panel on the right
      categoryLayout: {
        order: [],      // category ids in the order they should appear in the POS left menu
        hiddenIds: [],  // category ids hidden from the POS left menu entirely
      },
      theme: {
        ...POS_DEFAULT_TERMINAL_THEME,
      },
      categoryAppearance: {},
      homeShortcuts: buildDefaultPosHomeShortcuts(),
    },
    salesPoints: [], // named cash-register points per location (Verkaufspunkte) — name + room only, no per-point layout
    terminals: {
      devices: [
        { id: 'pos', name: 'POS terminal', type: 'Kassa', status: 'Live' },
        { id: 'webshop', name: 'Webshop connector', type: 'Web', status: 'Live' },
        { id: 'foodora', name: 'Foodora connector', type: 'Partner', status: 'Configured' },
        { id: 'lieferando', name: 'Lieferando connector', type: 'Partner', status: 'Configured' },
      ],
      webhookSecret: '',
    },
    loyalty: {
      cardsEnabled: true,
      stampGoal: 10,
      rewardTitle: '10 Stempel = Dessert gratis',
      issuedCards: [],
      coupons: [
        { id: 'coupon-welcome', code: 'WELCOME10', value: '10% Rabatt', active: true },
      ],
      transactions: [],
    },
  };
}

function sanitizePortalConfig(config, settings, user) {
  const companyName = settings?.company_name || user?.name || 'FiscalPOS';
  const locationName = settings?.company_city || '';
  const defaults = buildDefaultPortalConfig(settings, user);
  const merged = mergeDeep(defaults, config || {});
  const safeSlug = sanitizeWebshopSlug(merged?.webshop?.urlSlug, companyName, locationName);

  return {
    ...merged,
    salesCatalog: normalizeSalesCatalog(merged.salesCatalog || buildEmptySalesCatalog()),
    posTerminal: {
      ...merged.posTerminal,
      designPreset: merged?.posTerminal?.designPreset || 'gastronovi_classic',
      homeShortcuts: normalizePosHomeShortcuts(merged?.posTerminal?.homeShortcuts),
    },
    webshop: {
      ...merged.webshop,
      urlSlug: safeSlug,
      currentUrl: buildWebshopUrl(safeSlug),
    },
    orderSettings: {
      ...merged.orderSettings,
      thirdParty: {
        ...merged.orderSettings?.thirdParty,
        webshop: {
          ...(merged.orderSettings?.thirdParty?.webshop || {}),
          storeId: safeSlug,
        },
      },
    },
  };
}

export function useManagerPortalConfig() {
  const { user } = useAuth();
  const storageKey = `fiscalpos_manager_portal_config_${user?.company_id || 'default'}`;
  const [settings, setSettings] = useState(null);
  const [portalConfig, setPortalConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setIsLoading(true);
      try {
        const liveSettings = await getSettings();
        const stored = localStorage.getItem(storageKey);
        const storedConfig = stored ? JSON.parse(stored) : null;
        const remoteConfig = liveSettings?.manager_portal_config;
        const merged = sanitizePortalConfig(remoteConfig || storedConfig || {}, liveSettings, user);

        if (!mounted) return;
        setSettings(liveSettings);
        setPortalConfig(merged);
        localStorage.setItem(storageKey, JSON.stringify(merged));
      } catch (error) {
        const defaults = sanitizePortalConfig({}, {}, user);
        if (!mounted) return;
        setSettings({});
        setPortalConfig(defaults);
        localStorage.setItem(storageKey, JSON.stringify(defaults));
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [storageKey, user]);

  const savePortalConfig = async (updater, message = 'Manager-Einstellungen gespeichert') => {
    const rawNext =
      typeof updater === 'function'
        ? updater(portalConfig || buildDefaultPortalConfig(settings || {}, user))
        : updater;
    const next = sanitizePortalConfig(rawNext, settings || {}, user);

    setPortalConfig(next);
    localStorage.setItem(storageKey, JSON.stringify(next));

    try {
      const response = await updateSettings({ manager_portal_config: next });
      setSettings((previous) => ({ ...(previous || {}), manager_portal_config: next }));

      if (message) {
        toast.success(
          response?.manager_portal_config_saved === false
            ? `${message} (lokale Vorschau aktiv)`
            : message,
        );
      }
      return response;
    } catch (error) {
      toast.error('Speichern der Manager-Konfiguration fehlgeschlagen');
      return { success: false };
    }
  };

  const saveSettingsPatch = async (patch, message = 'Stammdaten aktualisiert') => {
    try {
      await updateSettings(patch);
      setSettings((previous) => ({ ...(previous || {}), ...patch }));
      toast.success(message);
      return true;
    } catch (error) {
      toast.error('Speichern der Stammdaten fehlgeschlagen');
      return false;
    }
  };

  return {
    settings,
    portalConfig,
    isLoading,
    savePortalConfig,
    saveSettingsPatch,
    weekdays: WEEKDAYS,
  };
}
